import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import CommonLink from 'components_v2/CommonLink'
import { PageLayout } from 'components_v2/SupportPages/__shared__'
import { Box, Button, Flex, H2, H5, P, Tag } from 'ui/bend'
import { Image as ImageAcro } from 'ui/acro'

import EventDetailBanner from './EventDetailBanner'
import EventLocationMap from './EventLocationMap'
import EventRegisterButton from './EventRegisterButton'
import {
  formatEventAddressLines,
  formatEventDate,
  formatEventLocation,
  formatEventTimeRange,
  isOnlineEvent
} from './formatEvent'

const BackLink = styled(CommonLink)(
  css({
    display: 'inline-flex',
    alignItems: 'center',
    color: 'grey4',
    fontSize: '14px',
    textDecoration: 'none',
    mb: '24px',
    '&:hover': {
      color: 'primary'
    }
  })
)

const ContentGrid = styled(Flex)(
  css({
    flexDirection: ['column', null, 'row'],
    gap: ['32px', null, '48px'],
    alignItems: 'flex-start'
  })
)

const MainColumn = styled(Box)(
  css({
    flex: 1,
    minWidth: 0
  })
)

const Sidebar = styled(Box)(
  css({
    width: ['100%', null, '320px'],
    flexShrink: 0
  })
)

const SidebarCard = styled(Box)(
  css({
    p: '24px',
    border: '1px solid',
    borderColor: 'grey2',
    backgroundColor: 'base'
  })
)

const SectionTitle = styled(H5)(
  css({
    m: 0,
    mb: '16px',
    color: 'primary'
  })
)

const MetaItem = styled(P)(
  css({
    m: 0,
    color: 'grey4',
    fontSize: '15px',
    lineHeight: '160%'
  })
)

const Description = styled(Box)(
  css({
    color: 'primary',
    fontSize: '16px',
    lineHeight: '175%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  })
)

const HostRow = styled(Flex)(
  css({
    alignItems: 'center',
    gap: '12px',
    mb: '12px'
  })
)

const HostAvatar = styled(Box)(
  css({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: 'grey2'
  })
)

const AddressLine = styled(P)(
  css({
    m: 0,
    color: 'primary',
    fontSize: '15px',
    lineHeight: '160%'
  })
)

const TagRow = styled(Flex)(
  css({
    flexWrap: 'wrap',
    gap: '8px',
    mt: '16px'
  })
)

const SidebarActionCard = styled(SidebarCard)(
  css({
    display: ['none', null, 'block'],
    mb: '16px'
  })
)

const MobileOnly = styled(Box)(
  css({
    display: ['block', null, 'none'],
    mt: '20px'
  })
)

const DesktopOnly = styled(Box)(
  css({
    display: ['none', null, 'block']
  })
)

const EventDetailPage = ({ event, onRegistrationChange }) => {
  const {
    title,
    description,
    coverUrl,
    startAt,
    endAt,
    timezone,
    tags = [],
    hosts = [],
    isPast,
    meetingUrl
  } = event

  const dateLabel = formatEventDate(startAt, timezone)
  const timeLabel = formatEventTimeRange(startAt, endAt, timezone)
  const locationSummary = formatEventLocation(event)
  const addressLines = formatEventAddressLines(event)
  const online = isOnlineEvent(event)

  return (
    <>
      <EventDetailBanner coverUrl={coverUrl} title={title} />

      <PageLayout>
        <Box py={['32px', null, '48px']}>
          <BackLink to='/events'>← Back to Community Events</BackLink>

          <Flex flexDirection='column' gap='0' mb='32px'>
            <Box>
              {isPast && (
                <Tag mb='12px' display='inline-block'>
                  Past event
                </Tag>
              )}
              <H2
                as='h1'
                m={0}
                color='primary'
                fontSize={['28px', null, '36px']}
              >
                {title}
              </H2>

              <Box mt='16px'>
                {dateLabel && <MetaItem>{dateLabel}</MetaItem>}
                {timeLabel && <MetaItem>{timeLabel}</MetaItem>}
                {locationSummary && <MetaItem>{locationSummary}</MetaItem>}
              </Box>

              {tags.length > 0 && (
                <TagRow>
                  {tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </TagRow>
              )}
            </Box>

            <MobileOnly>
              <EventRegisterButton
                {...event}
                eventId={event.id}
                eventTitle={event.title}
                registrationQuestions={event.registrationQuestions}
                registrationDefaults={event.registrationDefaults}
                registrationStatus={event.registrationStatus}
                isPast={isPast}
                size='compact'
                fullWidth
                onRegistrationChange={onRegistrationChange}
              />
            </MobileOnly>
          </Flex>

          <ContentGrid>
            <MainColumn>
              <SectionTitle as='h2'>About this event</SectionTitle>
              {description ? (
                <Description>{description}</Description>
              ) : (
                <MetaItem>No description provided.</MetaItem>
              )}
            </MainColumn>

            <Sidebar>
              <DesktopOnly>
                <SidebarActionCard>
                  <EventRegisterButton
                    {...event}
                    eventId={event.id}
                    eventTitle={event.title}
                    registrationQuestions={event.registrationQuestions}
                    registrationDefaults={event.registrationDefaults}
                    registrationStatus={event.registrationStatus}
                    isPast={isPast}
                    size='compact'
                    fullWidth
                    onRegistrationChange={onRegistrationChange}
                  />
                </SidebarActionCard>
              </DesktopOnly>

              <SidebarCard mb='16px'>
                <SectionTitle as='h3'>Location</SectionTitle>
                {online ? (
                  <>
                    <AddressLine>Online event</AddressLine>
                    {meetingUrl && (
                      <Button
                        as='a'
                        href={meetingUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        variant='ghost'
                        mt='16px'
                        width='100%'
                      >
                        Join online
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {addressLines.map((line) => (
                      <AddressLine key={line} mb='8px'>
                        {line}
                      </AddressLine>
                    ))}
                    <EventLocationMap event={event} />
                  </>
                )}
              </SidebarCard>

              {hosts.length > 0 && (
                <SidebarCard>
                  <SectionTitle as='h3'>Hosted by</SectionTitle>
                  {hosts.map((host) => (
                    <HostRow key={host.id || host.name}>
                      <HostAvatar>
                        {host.avatarUrl ? (
                          <ImageAcro
                            src={host.avatarUrl}
                            alt={host.name || 'Host'}
                            width='40px'
                            height='40px'
                            objectFit='cover'
                          />
                        ) : null}
                      </HostAvatar>
                      <AddressLine>{host.name || 'Host'}</AddressLine>
                    </HostRow>
                  ))}
                </SidebarCard>
              )}
            </Sidebar>
          </ContentGrid>
        </Box>
      </PageLayout>
    </>
  )
}

EventDetailPage.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    coverUrl: PropTypes.string,
    startAt: PropTypes.string,
    endAt: PropTypes.string,
    timezone: PropTypes.string,
    locationType: PropTypes.string,
    city: PropTypes.string,
    region: PropTypes.string,
    venue: PropTypes.string,
    country: PropTypes.string,
    fullAddress: PropTypes.string,
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meetingUrl: PropTypes.string,
    hosts: PropTypes.arrayOf(PropTypes.object),
    tags: PropTypes.arrayOf(PropTypes.string),
    isPast: PropTypes.bool,
    registrationStatus: PropTypes.string,
    registrationQuestions: PropTypes.array,
    registrationDefaults: PropTypes.object
  }).isRequired,
  onRegistrationChange: PropTypes.func
}

export default EventDetailPage
